import { createSignal, Show, Suspense, createMemo } from "solid-js";
import { useParams, useNavigate, createAsync, type RouteDefinition } from "@solidjs/router";
import { Title } from "@solidjs/meta";
import { AppShell } from "~/components/layout";
import { Tabs, TabsList, TabsTrigger, TabsContent, Button, Skeleton, Breadcrumbs } from "~/components/ui";
import {
  VideoPlayer,
  TranscriptView,
  VisualsGallery,
  AnnotationsPanel,
  VideoOverview,
} from "~/components/video";
import { getVideo, getTranscript, getVisuals, getPredictions } from "~/server/bff";
import { ArrowLeft, FileText, Image, Info } from "lucide-solid";

// Route preloading - starts loading before component renders
export const route = {
  preload: ({ params }) =>
    params.id
      ? Promise.all([getVideo(params.id as string), getTranscript(params.id as string), getVisuals(params.id as string)])
      : Promise.resolve(),
} satisfies RouteDefinition;

export default function VideoWorkbenchPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = createSignal(0);
  const [activeTab, setActiveTab] = createSignal("overview");

  // Server data via createAsync - SSR streamed
  const video = createAsync(() => getVideo(params.id || ""), { deferStream: true });
  const transcript = createAsync(() => getTranscript(params.id || ""), { deferStream: true });
  const visuals = createAsync(() => getVisuals(params.id || ""), { deferStream: true });
  
  // Predictions depend on video's channelId
  const predictions = createAsync(
    () => {
      const v = video();
    if (!v?.channelId) return Promise.resolve([]);
    return getPredictions({ channelId: v.channelId });
  });

  const videoPredictions = createMemo(() =>
    predictions()?.filter((p) => p.videoId === params.id) || []
  );

  const handleSeek = (time: number) => {
    setCurrentTime(time);
  };

  return (
    <AppShell>
      <Title>{video()?.title ?? "Video"} - IntelAnalyst</Title>
      <h1 class="sr-only">{video()?.title ?? "Video"}</h1>
      <div class="flex h-[calc(100vh-4rem)] flex-col">
        {/* Header with Breadcrumbs */}
        <div class="flex items-center gap-2 border-b px-4 py-2">
          <Suspense fallback={<Skeleton class="h-5 w-64" />}>
            <Show when={video()}>
              {(v) => (
                <Breadcrumbs
                  items={[
                    { label: "Library", href: "/explore/library" },
                    { label: v().channel?.name ?? "Channel", href: v().channelId ? `/explore/sources/${v().channelId}` : undefined },
                    { label: v().title },
                  ]}
                  showHome
                />
              )}
            </Show>
          </Suspense>
        </div>

        {/* Main content - wrapped in Suspense for streaming */}
        <Suspense
          fallback={
            <div class="flex-1 flex items-center justify-center">
              <div class="space-y-4 w-full max-w-4xl p-6">
                <Skeleton class="h-64 w-full rounded-lg" />
                <div class="flex gap-4">
                  <Skeleton class="h-10 w-24" />
                  <Skeleton class="h-10 w-24" />
                  <Skeleton class="h-10 w-24" />
                </div>
                <Skeleton class="h-32 w-full" />
              </div>
            </div>
          }
        >
          <Show when={video()}>
            {(v) => (
              <div class="flex flex-1 overflow-hidden">
                <div class="flex flex-1 flex-col overflow-hidden">
                  <VideoPlayer
                    videoUrl={v().url}
                    thumbnailUrl={v().thumbnailUrl}
                    onTimeUpdate={setCurrentTime}
                  />

                  <div class="flex-1 overflow-hidden border-t">
                    <Tabs value={activeTab()} onChange={setActiveTab} class="h-full flex flex-col">
                      <TabsList class="mx-4 mt-2 w-fit">
                        <TabsTrigger value="overview">
                          <Info class="h-4 w-4 mr-1" />
                          Overview
                        </TabsTrigger>
                        <TabsTrigger value="transcript">
                          <FileText class="h-4 w-4 mr-1" />
                          Transcript
                        </TabsTrigger>
                        <TabsTrigger value="visuals">
                          <Image class="h-4 w-4 mr-1" />
                          Visuals
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="overview" class="flex-1 overflow-auto">
                        <VideoOverview video={v()} />
                      </TabsContent>

                      <TabsContent value="transcript" class="flex-1 overflow-hidden">
                        <Suspense fallback={<TranscriptSkeleton />}>
                          <Show when={transcript()}>
                            {(chunks) => (
                              <TranscriptView
                                chunks={chunks()}
                                currentTime={currentTime()}
                                onSeek={handleSeek}
                              />
                            )}
                          </Show>
                        </Suspense>
                      </TabsContent>

                      <TabsContent value="visuals" class="flex-1 overflow-hidden">
                        <Suspense fallback={<VisualsSkeleton />}>
                          <Show when={visuals()}>
                            {(visualsList) => (
                              <VisualsGallery visuals={visualsList()} onSeek={handleSeek} />
                            )}
                          </Show>
                        </Suspense>
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>

                <div class="hidden w-80 border-l lg:block">
                  <AnnotationsPanel
                    videoId={params.id || ""}
                    predictions={videoPredictions()}
                    onAddToDossier={() => navigate(`/investigate/dossiers?addVideo=${params.id}`)}
                    onAskChat={() => navigate(`/chat?video=${params.id}`)}
                  />
                </div>
              </div>
            )}
          </Show>
        </Suspense>
      </div>
    </AppShell>
  );
}

function TranscriptSkeleton() {
  return (
    <div class="p-4 space-y-3">
      <Skeleton class="h-4 w-full" />
      <Skeleton class="h-4 w-5/6" />
      <Skeleton class="h-4 w-4/5" />
      <Skeleton class="h-4 w-full" />
      <Skeleton class="h-4 w-3/4" />
    </div>
  );
}

function VisualsSkeleton() {
  return (
    <div class="p-4 grid grid-cols-3 gap-4">
      <Skeleton class="aspect-video rounded-md" />
      <Skeleton class="aspect-video rounded-md" />
      <Skeleton class="aspect-video rounded-md" />
      <Skeleton class="aspect-video rounded-md" />
      <Skeleton class="aspect-video rounded-md" />
      <Skeleton class="aspect-video rounded-md" />
    </div>
  );
}
